import React, { useEffect, useMemo, useState } from "react";
import Loader from "../components/Loader";
import { Helmet } from "react-helmet";
import Newsletter from "../components/Newsletter";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "../components/Breadcrumbs";
import ProductCard from "../components/ProductCard";
import { useQuery } from "@tanstack/react-query";
import { fetchAllProducts, fetchCollections, fetchCategories } from "../lib/medusa";
import Modal from "react-bootstrap/Modal";
import Accordion from "react-bootstrap/Accordion";
// Top category rail swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper";
import "swiper/css";
import "swiper/css/free-mode";
import StructuredData from "../seo/StructuredData";
import { breadcrumbJsonLd, itemListJsonLd } from "../seo/jsonld";
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from "../seo/site";
import {
  CATEGORY_GROUP_MAP,
  categoryMatchesSelection,
  groupCategoriesForDisplay,
} from "../utils/categoryGroups";

export default function AllProducts({ setHeader }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [limit, setLimit] = useState(12);
  const [offset, setOffset] = useState(0);
  const [isOverlayVisible, setIsOverlayVisible] = useState(true);
  const [products, setProducts] = useState([]);
  const [count, setCount] = useState(0);
  // store friendly values in URL/state: collection handles and category slugs
  const [selectedCollectionHandles, setSelectedCollectionHandles] = useState([]);
  const [selectedCategorySlugs, setSelectedCategorySlugs] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [brandQuery, setBrandQuery] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const pageLang = i18n?.language === "me" ? "me" : "en";
  const pageUrl = `${SITE_URL}${location.pathname}`;
  const pageTitle = `${t("header.products.pageTitle")} - ${SITE_NAME}`;
  const pageDescription = t("header.products.metaDescription");
  const [showLeftCatArrow, setShowLeftCatArrow] = useState(false);
  const [showRightCatArrow, setShowRightCatArrow] = useState(true);
  const swiperRef = React.useRef(null);

  const itemsLabel = (n) => {
    if (i18n.language === 'me') {
      const lastTwo = n % 100;
      const last = n % 10;
      const word = (last === 1 && lastTwo !== 11) ? 'proizvod' : 'proizvoda';
      return `${n} ${word}`;
    }
    return `${n} ${t('items', { defaultValue: 'items' })}`;
  };

  let sort = searchParams.get("sort");
  const pageParam = parseInt(searchParams.get("page") || "1", 10);
  const collectionsParam = searchParams.get("collections") || ""; // handles
  const categoriesParam = searchParams.get("categories") || ""; // slugs

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: [
      "all-products",
      sort,
      limit,
      offset,
      selectedCollectionHandles,
      selectedCategorySlugs,
    ],
    queryFn: () => {
      // Compute IDs here after collections/categories queries are defined
      const collectionIds = (collections || [])
        .filter((c) => selectedCollectionHandles.includes(c.handle))
        .map((c) => c.id);
      const categoryIds = (categories || [])
        .filter((cat) => categoryMatchesSelection(cat, selectedCategorySlugs, t))
        .map((cat) => cat.id);
      return fetchAllProducts(sort, limit, offset, collectionIds, categoryIds);
    },
    keepPreviousData: true,
    onSuccess: (data) => {
      if (data?.length) {
        setProducts(data[0]);
        setCount(data[1]);
        window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
        window.dataLayer.push({
          event: "view_item_list",
          ecommerce: {
            item_list_id: "related_products",
            item_list_name: "Related products",
            items: data[0].map((product, index) => ({
              item_id: product.id,
              item_name: product.title,
              affiliation: "DURO",
              index: index,
              item_brand: product?.collection?.title,
              item_category: product?.categories?.[0]?.name,
              item_list_id: "related_products",
              item_list_name: "Related products",
              item_variant: product?.variants?.[0]?.title,
              location_id: "",
              price: product.variants?.[0]?.prices?.[0]?.amount,
              quantity: product.variants?.[0]?.inventory_quantity,
            })),
          },
        });
      }
    },
  });

  const { data: collections = [] } = useQuery({
    queryKey: ["collections"],
    queryFn: fetchCollections,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  // Helpers: i18n-aware category display
  const categoryOptions = useMemo(
    () => groupCategoriesForDisplay(categories, t),
    [categories, t]
  );

  useEffect(() => {
    refetch();
  }, [offset, sort, limit, selectedCollectionHandles, selectedCategorySlugs, collections, categories, refetch]);

  // Sync state from URL params
  useEffect(() => {
    const page = isNaN(pageParam) || pageParam < 1 ? 1 : pageParam;
    setOffset((page - 1) * limit);
    setSelectedCollectionHandles(
      collectionsParam ? collectionsParam.split(",").filter(Boolean) : []
    );

    const rawSlugs = categoriesParam
      ? categoriesParam.split(",").filter(Boolean)
      : [];

    if (!rawSlugs.length) {
      setSelectedCategorySlugs([]);
      return;
    }

    const normalized = rawSlugs
      .map((slug) => CATEGORY_GROUP_MAP[slug] || slug)
      .map((slug) => {
        const option = categoryOptions.find(
          (opt) => opt.slug === slug || opt.canonicalSlug === slug
        );
        return option ? option.slug : slug;
      });

    setSelectedCategorySlugs(Array.from(new Set(normalized)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageParam, collectionsParam, categoriesParam, categoryOptions]);

  const totalPages = Math.ceil(count / limit);
  const currentPage = Math.floor(offset / limit) + 1;
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages || offset + limit >= count;
  const hasSinglePage = totalPages <= 1;

  const handleNextPage = () => {
    if (offset + limit < count) {
      const nextOffset = offset + limit;
      setOffset(nextOffset);
      const params = new URLSearchParams(searchParams.toString());
      params.set("page", String(Math.floor(nextOffset / limit) + 1));
      setSearchParams(params);
    }
  };

  const handlePreviousPage = () => {
    if (offset > 0) {
      const prevOffset = offset - limit;
      setOffset(prevOffset);
      const params = new URLSearchParams(searchParams.toString());
      params.set("page", String(Math.floor(prevOffset / limit) + 1));
      setSearchParams(params);
    }
  };

  const handleFirstPage = () => {
    setOffset(0);
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", "1");
    setSearchParams(params);
  };

  const handleLastPage = () => {
    const lastOffset = limit * (totalPages - 1);
    setOffset(lastOffset);
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", String(totalPages));
    setSearchParams(params);
  };

  useEffect(() => {
    const handleScroll = () => {
      const bottomOffset = 400;
      const reachedNearBottom =
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - bottomOffset;

      setIsOverlayVisible(!reachedNearBottom);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    sort = searchParams.get("sort");
    refetch();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 1) {
        setHeader(true);
      } else {
        setHeader(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [setHeader]);

  const sortBy = (input) => {
    const params = new URLSearchParams(searchParams.toString());
    if (params.get("sort") === input) {
      params.delete("sort");
    } else {
      params.set("sort", input);
    }
    params.set("page", "1");
    setSearchParams(params);
    setOffset(0);
    refetch();
  };

  const toggleCollection = (handle) => {
    setSelectedCollectionHandles((prev) => {
      const exists = prev.includes(handle);
      const next = exists ? prev.filter((x) => x !== handle) : [...prev, handle];
      const params = new URLSearchParams(searchParams.toString());
      params.set("collections", next.join(","));
      params.set("page", "1");
      setSearchParams(params);
      setOffset(0);
      return next;
    });
  };

  const toggleCategory = (slug) => {
    setSelectedCategorySlugs((prev) => {
      // single-select behavior
      const next = prev.length === 1 && prev[0] === slug ? [] : [slug];
      const params = new URLSearchParams(searchParams.toString());
      if (next.length) params.set("categories", next[0]); else params.delete("categories");
      params.set("page", "1");
      setSearchParams(params);
      setOffset(0);
      return next;
    });
  };

  const clearFilters = () => {
    setSelectedCategorySlugs([]);
    setSelectedCollectionHandles([]);
    const params = new URLSearchParams(searchParams.toString());
    params.delete("categories");
    params.delete("collections");
    params.set("page", "1");
    setSearchParams(params);
    setOffset(0);
  };
  return (
    <>
      <Helmet htmlAttributes={{ lang: pageLang }}>
        {/* <!-- meta tag --> */}
        <meta charset="utf-8" />
        <title>
          {pageTitle}
        </title>

        {/* <!-- Meta Tags--> */}
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="durō, duro, niche perfumery, niche parfimerija,  parfimerija, galerija"
        />
        <meta name="author" content="intellect.se" />

        {/* <!-- responsive tag --> */}
        <meta http-equiv="x-ua-compatible" content="ie=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* <!-- Facebook Metadata --> */}
        <meta property="og:type" content="website" />
        <meta property="og:image:height" content="1200" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:image" content={DEFAULT_OG_IMAGE} />
      </Helmet>
      {/* hreflang + canonical */}
      <Helmet>
        <link
          rel="canonical"
          href={`https://parfimerija-duro.me/${i18n.language}/${i18n.language === 'me' ? 'proizvodi' : 'products'}`}
        />
        <link rel="alternate" href={`https://parfimerija-duro.me/en/products`} hreflang="en" />
        <link rel="alternate" href={`https://parfimerija-duro.me/me/proizvodi`} hreflang="me" />
        <link rel="alternate" href={`https://parfimerija-duro.me/en/products`} hreflang="x-default" />
      </Helmet>
      <Loader />
      {/* // <!-- Main content Start --> */}
      <div className="main-content">
        <div className="rs-breadcrumbs img13">
          <div className="container">
            <div className="breadcrumbs-inner">
              <h1 className="page-title">{t("header.products.pageTitle")}</h1>
            </div>
          </div>
        </div>
        <Breadcrumbs />

        {/* <div className="rs-team style7 blue-bg height-ctrl md-pb-30 md-pt-30">
          <div
            className="container container-fixwidth"
            style={{ display: isOverlayVisible ? "block" : "none" }}
          >
            <div className="collection-overlay gray-bg4">
              <p className="collection-overlay-text">
                {products?.length} {t("brands.collectionProducts")}
              </p>
              <div class="btn-online">
                <Link to="#" className="buy-online-button">
                  <span className="btn-txt">{t("brands.allProducts")}</span>
                  <i className="fa fa-cart-plus"></i>
                </Link>
              </div>
            </div>
          </div>
        </div> */}

        {/* <!-- Products Section Start --> */}
        {isLoading && <div className="loading-text lion-bg">Loading...</div>}
        {!isLoading && products?.length > 0 ? (
          <div className="rs-team style7 lion-bg height-ctrl md-pb-20 md-pt-20 product-load">
            <div className="container position-relative">
              <div className="row">
                {/* Filters Left Column */}
                <div className="d-none filter-section">
                  <div className="filter-section filter-sidebar">
                    <Accordion alwaysOpen defaultActiveKey={["brands","categories"]}>
                      <Accordion.Item eventKey="sort">
                        <Accordion.Header>{t("products.sortBy")}</Accordion.Header>
                        <Accordion.Body>
                          <div className="yoox-sort-select">
                            <select
                              className="form-select"
                              value={searchParams.get("sort") || ""}
                              onChange={(e)=>sortBy(e.target.value)}
                            >
                              <option value="">{t("products.options.popular")}</option>
                              <option value="latest">{t("products.options.latest_arrivals")}</option>
                              <option value="price_low">{t("products.options.price_low_high")}</option>
                              <option value="price_high">{t("products.options.price_high_low")}</option>
                            </select>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                      <Accordion.Item eventKey="brands">
                        <Accordion.Header>{t("filters.brands", {defaultValue:"Brands"})}</Accordion.Header>
                        <Accordion.Body>
                          <form className="newsletter-form mb-2" onSubmit={(e)=>e.preventDefault()}>
                            <input type="text" placeholder={t("filters.searchBrands", {defaultValue:"Search brands"})} value={brandQuery} onChange={(e)=>setBrandQuery(e.target.value)} />
                          </form>
                          <ul className="sort-options" style={{maxHeight:'240px', overflowY:'auto'}}>
                            {collections.filter((c)=>c.title.toLowerCase().includes(brandQuery.toLowerCase())).map((c)=>(
                              <li key={c.id}>
                                <label className="d-flex align-items-center" style={{gap:'8px'}}>
                                  <input type="checkbox" checked={selectedCollectionHandles.includes(c.handle)} onChange={() => toggleCollection(c.handle)} />
                                  <span>{c.title}</span>
                                </label>
                              </li>
                            ))}
                          </ul>
                        </Accordion.Body>
                      </Accordion.Item>
                      <Accordion.Item eventKey="categories">
                        <Accordion.Header>{t("filters.categories", {defaultValue:"Categories"})}</Accordion.Header>
                        <Accordion.Body>
                          <ul className="sort-options" style={{maxHeight:'240px', overflowY:'auto'}}>
                            {categoryOptions.map((option) => (
                              <li key={option.slug}>
                                <label className="d-flex align-items-center" style={{gap:'8px'}}>
                                  <input
                                    type="checkbox"
                                    checked={selectedCategorySlugs.includes(option.slug)}
                                    onChange={() => toggleCategory(option.slug)}
                                  />
                                  <span>{option.label}</span>
                                </label>
                              </li>
                            ))}
                          </ul>
                          <div className="mt-3">
                            <div className="sort-label mb-1">{t("filters.price", {defaultValue:"Price"})}</div>
                            <div className="d-flex" style={{gap:'8px'}}>
                              <input type="number" className="form-control" placeholder={t("filters.min", {defaultValue:"Min"})} value={priceMin} onChange={(e)=>setPriceMin(e.target.value)} style={{maxWidth:'100px'}} />
                              <input type="number" className="form-control" placeholder={t("filters.max", {defaultValue:"Max"})} value={priceMax} onChange={(e)=>setPriceMax(e.target.value)} style={{maxWidth:'100px'}} />
                            </div>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </div>
                </div>
                <div className="col-12 products-section">
                  <div className="sec-title mb-10 mt-10 md-pb-10 md-pt-10">
                    <h2 className="title pb-10">{t("products.sub-title")}</h2>
                    <div className="heading-border-line left-style"></div>
                  </div>

                  {/* Top Categories rail (clickable chips) */}
                  {categoryOptions.length > 0 && (
                    <div className="category-rail mb-3 position-relative">
                      {showLeftCatArrow && (
                        <button
                          className="cat-nav left"
                          onClick={() => {
                            if (swiperRef.current) {
                              swiperRef.current.slidePrev();
                            }
                          }}
                          aria-label="Prev"
                        >
                          ‹
                        </button>
                      )}
                      {showRightCatArrow && (
                        <button
                          className="cat-nav right"
                          onClick={() => {
                            if (swiperRef.current) {
                              setShowLeftCatArrow(true);
                              swiperRef.current.slideNext();
                            }
                          }}
                          aria-label="Next"
                        >
                          ›
                        </button>
                      )}
                      <Swiper
                        modules={[FreeMode]}
                        freeMode
                        slidesPerView="auto"
                        spaceBetween={10}
                        className="category-swiper"
                        onBeforeInit={(sw) => { swiperRef.current = sw; }}
                        onReachEnd={() => setShowRightCatArrow(false)}
                        onFromEdge={() => setShowRightCatArrow(true)}
                        onReachBeginning={() => setShowLeftCatArrow(false)}
                      >
                        {/* View all */}
                        <SwiperSlide key="view-all">
                          <button
                            className={`category-link ${selectedCategorySlugs.length === 0 ? 'active' : ''}`}
                            onClick={() => { setSelectedCategorySlugs([]); setOffset(0); }}
                          >
                            {t('viewAll', { defaultValue: i18n.language === 'me' ? 'Svi proizvodi' : 'View all' })}
                          </button>
                        </SwiperSlide>
                        {categoryOptions.map((option) => {
                          const { slug, label } = option;
                          const active = selectedCategorySlugs.includes(slug);
                          return (
                            <SwiperSlide key={slug}>
                              <button
                                className={`category-link ${active ? 'active' : ''}`}
                                onClick={()=>toggleCategory(slug)}
                              >
                                {label}
                              </button>
                            </SwiperSlide>
                          );
                        })}
                      </Swiper>
                    </div>
                  )}

                  {/* count + filter bar */}
                  <div className="d-flex justify-content-between align-items-center mb-2 border-top pt-2">
                    <div className="items-count">{itemsLabel(count)}</div>
                    <div className="ms-auto d-flex align-items-center gap-3">
                      {(selectedCollectionHandles.length > 0 || selectedCategorySlugs.length > 0 || priceMin || priceMax) && (
                        <a className="clear-link d-none d-md-inline" onClick={clearFilters}>{t('actions.clear', { defaultValue: 'Clear' })}</a>
                      )}
                      <button className="pagination-btn" onClick={() => setShowFilters(true)}>{t('actions.filters', { defaultValue: 'Filteri' })}</button>
                    </div>
                  </div>
                  {/* Active filter chips */}
                  {(selectedCollectionHandles.length > 0 || selectedCategorySlugs.length > 0 || priceMin || priceMax) && (
                    <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
                      {selectedCollectionHandles.map((handle)=>{
                        const c = collections.find((x)=>x.handle===handle);
                        return c ? (
                          <span key={`chip-c-${handle}`} className="filter-chip">{c.title}<button onClick={()=>toggleCollection(handle)}>×</button></span>
                        ) : null;
                      })}
                      {selectedCategorySlugs.map((slug) => {
                        const option = categoryOptions.find((opt) => opt.slug === slug);
                        return option ? (
                          <span key={`chip-cat-${slug}`} className="filter-chip">
                            {option.label}
                            <button onClick={() => toggleCategory(slug)}>×</button>
                          </span>
                        ) : null;
                      })}
                      {(priceMin || priceMax) && (
                        <span className="filter-chip">{`${priceMin || 0} - ${priceMax || '∞'}`}<button onClick={()=>{setPriceMin("");setPriceMax("");}}>×</button></span>
                      )}
                      {/* Clear link moved to count/filter bar above to avoid duplication */}
                    </div>
                  )}
                  <ul className="product-wrapper">
                    {(products || []).filter((p)=>{
                      const amount = p?.variants?.[0]?.prices?.[0]?.amount || 0;
                      const inMin = priceMin ? amount >= Number(priceMin) : true;
                      const inMax = priceMax ? amount <= Number(priceMax) : true;
                      return inMin && inMax;
                    }).map((product, i) => (
                      <li className="col-md-4 col-6 product-item" key={i}>
                        <ProductCard product={product} />
                      </li>
                    ))}
                  </ul>
                  {isFetching && (
                    <div className="position-absolute top-0 start-0 w-100 h-100" style={{background:"rgba(0,0,0,0.05)"}}>
                      <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="spinner-border text-light" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* JSON-LD: Breadcrumb + ItemList */}
                  <StructuredData
                    data={[
                      breadcrumbJsonLd([
                        { name: i18n.language === 'me' ? 'Početna' : 'Home', item: `https://parfimerija-duro.me/${i18n.language}/` },
                        { name: i18n.language === 'me' ? 'Proizvodi' : 'Products', item: `https://parfimerija-duro.me/${i18n.language}/proizvodi` },
                      ]),
                      itemListJsonLd(
                        (products || []).map((p) => ({
                          name: p.title,
                          url: `https://shop.parfimerija-duro.me/${i18n.language}/products/${p.handle}`,
                          image: p.thumbnail || p.images?.[0]?.url,
                          brand: p.collection?.title,
                          category: p.categories?.[0]?.name,
                          price: (p?.variants?.[0]?.prices?.[0]?.amount || 0) / 100,
                          currency: p?.variants?.[0]?.prices?.[0]?.currency_code?.toUpperCase() || 'EUR',
                          inStock: (p?.variants?.[0]?.inventory_quantity ?? 1) > 0,
                        }))
                      ),
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Mobile Filters Modal */}
        <Modal show={showFilters} onHide={() => setShowFilters(false)} centered scrollable>
          <Modal.Header closeButton>
            <Modal.Title>{t("filters.title", { defaultValue: "Filters" })}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{background:'#a59278', color:'#fff'}}>
            <div className="filter-section">
              <span className="sort-label">{t("products.sortBy")}</span>
              <ul className="sort-options">
                <li><button class={`filter-btn ${searchParams.get("sort") === "popular" && "active"}`} onClick={() => sortBy("popular")}>
                  {t("products.options.popular")}
                </button></li>
                <li><button class={`filter-btn ${searchParams.get("sort") === "latest" && "active"}`} onClick={() => sortBy("latest")}>
                  {t("products.options.latest_arrivals")}
                </button></li>
                <li><button class={`filter-btn ${searchParams.get("sort") === "price_low" && "active"}`} onClick={() => sortBy("price_low")}>
                  {t("products.options.price_low_high")}
                </button></li>
                <li><button class={`filter-btn ${searchParams.get("sort") === "price_high" && "active"}`} onClick={() => sortBy("price_high")}>
                  {t("products.options.price_high_low")}
                </button></li>
              </ul>
              <hr />
              <span className="sort-label">{t("filters.brands", { defaultValue: "Brands" })}</span>
              <form className="newsletter-form mb-2" onSubmit={(e)=>e.preventDefault()}>
                <input type="text" placeholder={t("filters.searchBrands", {defaultValue:"Search brands"})} value={brandQuery} onChange={(e)=>setBrandQuery(e.target.value)} />
              </form>
              <ul className="sort-options" style={{maxHeight:'240px', overflowY:'auto'}}>
                {collections.filter((c)=>c.title.toLowerCase().includes(brandQuery.toLowerCase())).map((c) => (
                  <li key={c.id}>
                    <label className="d-flex align-items-center" style={{gap:'8px'}}>
                      <input
                        type="checkbox"
                        checked={selectedCollectionHandles.includes(c.handle)}
                        onChange={() => toggleCollection(c.handle)}
                      />
                      <span>{c.title}</span>
                    </label>
                  </li>
                ))}
              </ul>
              <span className="sort-label">{t("filters.categories", { defaultValue: "Categories" })}</span>
              <ul className="sort-options" style={{maxHeight:'240px', overflowY:'auto'}}>
                {categoryOptions.map((option) => (
                  <li key={option.slug}>
                    <label className="d-flex align-items-center" style={{gap:'8px'}}>
                      <input
                        type="checkbox"
                        checked={selectedCategorySlugs.includes(option.slug)}
                        onChange={() => toggleCategory(option.slug)}
                      />
                      <span>{option.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button className="pagination-btn" onClick={clearFilters}>{t("actions.clear", { defaultValue: "Clear" })}</button>
            <button className="pagination-btn" onClick={() => setShowFilters(false)}>{t("actions.apply", { defaultValue: "Apply" })}</button>
          </Modal.Footer>
        </Modal>

        <div className="pagination lion-bg">
          <div className="container gap-4 flex-row justify-content-center">
            <button
              className="pagination-btn"
              onClick={handleFirstPage}
              disabled={isFirstPage || hasSinglePage}
              aria-disabled={isFirstPage || hasSinglePage}
            >
              <i className="fa fa-angle-double-left"></i>{" "}
              {t("products.pagination.first")}
            </button>

            <button
              className="pagination-btn"
              onClick={handlePreviousPage}
              disabled={isFirstPage || hasSinglePage}
              aria-disabled={isFirstPage || hasSinglePage}
            >
              <i className="fa fa-angle-left"></i>
            </button>

            <span className="page">
              {t("products.pagination.page")} {currentPage}{" "}
              {t("products.pagination.of")} {totalPages}
            </span>

            <button
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={isLastPage || hasSinglePage}
              aria-disabled={isLastPage || hasSinglePage}
            >
              <i className="fa fa-angle-right"></i>{" "}
              {/* Font Awesome icon for next */}
            </button>

            <button
              className="pagination-btn"
              onClick={handleLastPage}
              disabled={isLastPage || hasSinglePage}
              aria-disabled={isLastPage || hasSinglePage}
            >
              {t("products.pagination.last")}{" "}
              <i className="fa fa-angle-double-right"></i>{" "}
              {/* Font Awesome icon for last */}
            </button>
          </div>
        </div>
        {/* <!-- Products Section End --> */}

        {/* <!-- Newsletter section start --> */}
        <div className="rs-newsletter lion-bg style1 pt-90 md-pt-60 mb--65">
          <Newsletter />
        </div>

        {/* <!-- Newsletter section end --> */}
      </div>
      {/* // <!-- Main content End --> */}
    </>
  );
}
